/**
 * Copy `text` to the clipboard with a legacy `execCommand` fallback.
 *
 * Port of platform/src/utils/clipboard.ts (PLATAFORMA-5) for the chat app
 * (Sentry CHAT-MARITACA-3B): iOS < 13.4 and some in-app webviews (Google
 * Search App, Android WebViews) expose no `navigator.clipboard` at all, so a
 * bare `navigator.clipboard.writeText(...)` throws a synchronous TypeError
 * inside the click handler — the `.catch()` chained on the never-created
 * promise cannot intercept it. The Async Clipboard API can also reject with
 * `NotAllowedError` (Safari permission denials, out-of-gesture calls).
 *
 * Returns `true` on success, `false` when both the async API and the legacy
 * `execCommand('copy')` path fail, so callers can decide what to show.
 * Never throws.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy fallback
  }

  try {
    if (typeof document === "undefined") return false;
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
    return ok;
  } catch {
    return false;
  }
}
